/**
 * SaaS Model Configuration
 * COMPLETE extraction of ALL 44 assumption fields from the existing SaaS model
 * 
 * Source: /server/saas/saas-api.js (defaultAssumptions)
 * Source: /public/js/saas-simple.js (loadAssumptionsForm)
 * 
 * FIELD COUNT VERIFICATION:
 * - Revenue Model: 5 fields
 * - Customer Growth: 7 fields  
 * - Cost of Goods Sold: 3 fields
 * - Team & Salaries: 3 fields
 * - Customer Acquisition: 2 fields
 * - Operating Expenses: 4 fields
 * - Depreciation & Amortization: 4 fields
 * - Debt & Interest: 3 fields
 * - Working Capital: 5 fields
 * - Capital Raising: 5 fields
 * - Financial & Tax: 2 fields
 * - User Details: 1 field (email)
 * TOTAL: 44 fields
 */

import type { ModelConfig } from '@/types/models';
import { standardResultTabs } from '@/config/resultTabs';

export const saasConfig: ModelConfig = {
  modelType: 'saas',
  apiEndpoint: '/api/saas',
  displayName: 'SaaS',
  description: 'Subscription-based software revenue model with MRR, ARR, and customer metrics.',
  icon: '💻',
  color: '#3b82f6', // Primary blue
  resultTabs: standardResultTabs,

  assumptionGroups: [
    // === GROUP 1: User Details ===
    {
      id: 'user-details',
      title: '👤 Your Details',
      fields: [
        {
          id: 'userEmail',
          label: 'Your Email (for updates)',
          type: 'email',
          placeholder: 'name@company.com',
          required: false,
        },
      ],
    },

    // === GROUP 2: Revenue Model ===
    {
      id: 'revenue-model',
      title: '💰 Revenue Model',
      fields: [
        {
          id: 'monthlyRevenuePerCustomer',
          label: 'Monthly Subscription Fee per Customer ($)',
          type: 'number',
          defaultValue: 485,
          min: 0,
          step: 1,
          helpText: 'Base monthly subscription fee per customer',
        },
        {
          id: 'usageFeePerCustomer',
          label: 'Additional Usage Fees per Customer ($)',
          type: 'number',
          defaultValue: 45,
          min: 0,
          step: 1,
          helpText: 'Additional monthly usage fees - API calls, storage, etc.',
        },
        {
          id: 'expansionRevenueRate',
          label: 'Expansion Revenue Rate (%)',
          type: 'number',
          defaultValue: 12,
          min: 0,
          max: 100,
          step: 0.1,
          helpText: 'Monthly expansion revenue as % of base revenue - upsells, add-ons',
        },
        {
          id: 'annualContractPct',
          label: 'Annual Contract % (%)',
          type: 'number',
          defaultValue: 35,
          min: 0,
          max: 100,
          step: 1,
          helpText: 'Percentage of customers paying annually',
        },
        {
          id: 'annualDiscountPct',
          label: 'Annual Discount % (%)',
          type: 'number',
          defaultValue: 15,
          min: 0,
          max: 100,
          step: 1,
          helpText: 'Discount given for annual payments',
        },
      ],
    },

    // === GROUP 3: Customer Growth ===
    {
      id: 'customer-growth',
      title: '👥 Customer Growth',
      fields: [
        {
          id: 'startingCustomers',
          label: 'Starting Customers',
          type: 'number',
          defaultValue: 75,
          min: 0,
          step: 1,
          helpText: 'Number of paying customers at start',
        },
        {
          id: 'growthRateY2',
          label: 'Growth Rate Year 2 (%)',
          type: 'number',
          defaultValue: 20,
          min: -100,
          max: 500,
          step: 0.1,
        },
        {
          id: 'growthRateY3',
          label: 'Growth Rate Year 3 (%)',
          type: 'number',
          defaultValue: 15,
          min: -100,
          max: 500,
          step: 0.1,
        },
        {
          id: 'growthRateY4',
          label: 'Growth Rate Year 4 (%)',
          type: 'number',
          defaultValue: 12,
          min: -100,
          max: 500,
          step: 0.1,
        },
        {
          id: 'growthRateY5',
          label: 'Growth Rate Year 5 (%)',
          type: 'number',
          defaultValue: 8,
          min: -100,
          max: 500,
          step: 0.1,
        },
        {
          id: 'monthlyChurnRate',
          label: 'Monthly Churn Rate (%)',
          type: 'number',
          defaultValue: 2.5,
          min: 0,
          max: 100,
          step: 0.1,
          helpText: 'Monthly customer churn rate',
        },
      ],
    },

    // === GROUP 4: Cost of Goods Sold ===
    {
      id: 'cogs',
      title: '📊 Cost of Goods Sold',
      fields: [
        {
          id: 'hostingCostPerCustomer',
          label: 'Monthly Hosting Cost per Customer ($)',
          type: 'number',
          defaultValue: 12,
          min: 0,
          step: 0.01,
          helpText: 'Monthly hosting/infrastructure cost per customer',
        },
        {
          id: 'supportCostPerCustomer',
          label: 'Monthly Support Cost per Customer ($)',
          type: 'number',
          defaultValue: 8,
          min: 0,
          step: 0.01,
          helpText: 'Monthly customer support cost per customer',
        },
        {
          id: 'transactionFeePct',
          label: 'Payment Processing Fees (% of Revenue)',
          type: 'number',
          defaultValue: 2.9,
          min: 0,
          max: 100,
          step: 0.01,
          helpText: 'Payment processing fees as % of revenue',
        },
      ],
    },

    // === GROUP 5: Team & Salaries ===
    {
      id: 'team-salaries',
      title: '👥 Team & Salaries',
      fields: [
        {
          id: 'startingTeamSize',
          label: 'Starting Team Size',
          type: 'number',
          defaultValue: 8,
          min: 1,
          step: 1,
          helpText: 'Initial team size (full-time employees)',
        },
        {
          id: 'avgAnnualSalary',
          label: 'Average Annual Salary ($)',
          type: 'number',
          defaultValue: 105000,
          min: 0,
          step: 1000,
          helpText: 'Average annual salary per employee',
        },
        {
          id: 'teamGrowthRate',
          label: 'Team Growth Rate (%)',
          type: 'number',
          defaultValue: 45,
          min: 0,
          max: 200,
          step: 1,
          helpText: 'Annual team growth rate',
        },
      ],
    },

    // === GROUP 6: Customer Acquisition ===
    {
      id: 'customer-acquisition',
      title: '🎯 Customer Acquisition',
      fields: [
        {
          id: 'cac',
          label: 'Customer Acquisition Cost ($)',
          type: 'number',
          defaultValue: 1850,
          min: 0,
          step: 1,
          helpText: 'Customer acquisition cost per customer',
        },
        {
          id: 'salesMarketingPct',
          label: 'Sales & Marketing (% of Revenue)',
          type: 'number',
          defaultValue: 38,
          min: 0,
          max: 100,
          step: 0.1,
        },
      ],
    },

    // === GROUP 7: Operating Expenses ===
    {
      id: 'operating-expenses',
      title: '💼 Operating Expenses',
      fields: [
        {
          id: 'monthlyTechCosts',
          label: 'Monthly Technology Costs ($)',
          type: 'number',
          defaultValue: 25000,
          min: 0,
          step: 100,
          helpText: 'Monthly technology costs - software, tools, infrastructure',
        },
        {
          id: 'rdPct',
          label: 'R&D (% of Revenue)',
          type: 'number',
          defaultValue: 18,
          min: 0,
          max: 100,
          step: 0.1,
        },
        {
          id: 'gaPct',
          label: 'General & Administrative (% of Revenue)',
          type: 'number',
          defaultValue: 12,
          min: 0,
          max: 100,
          step: 0.1,
        },
        {
          id: 'monthlyOfficeCosts',
          label: 'Monthly Office Costs ($)',
          type: 'number',
          defaultValue: 8000,
          min: 0,
          step: 100,
          helpText: 'Monthly office rent and utilities',
        },
      ],
    },

    // === GROUP 8: Depreciation & Amortization ===
    {
      id: 'depreciation-amortization',
      title: '📈 Depreciation & Amortization',
      fields: [
        {
          id: 'equipmentDepreciationRate',
          label: 'Equipment Depreciation Rate (%)',
          type: 'number',
          defaultValue: 20,
          min: 0,
          max: 100,
          step: 1,
          helpText: 'Annual depreciation rate for equipment',
        },
        {
          id: 'softwareAmortizationRate',
          label: 'Software Amortization Rate (%)',
          type: 'number',
          defaultValue: 33,
          min: 0,
          max: 100,
          step: 1,
          helpText: 'Annual amortization rate for software/IP',
        },
        {
          id: 'initialEquipmentValue',
          label: 'Initial Equipment Value ($)',
          type: 'number',
          defaultValue: 150000,
          min: 0,
          step: 1000,
          helpText: 'Initial equipment and hardware value',
        },
        {
          id: 'initialSoftwareValue',
          label: 'Initial Software Value ($)',
          type: 'number',
          defaultValue: 200000,
          min: 0,
          step: 1000,
          helpText: 'Initial software and IP value',
        },
      ],
    },

    // === GROUP 9: Debt & Interest ===
    {
      id: 'debt-interest',
      title: '🏦 Debt & Interest',
      fields: [
        {
          id: 'initialDebtAmount',
          label: 'Initial Debt Amount ($)',
          type: 'number',
          defaultValue: 500000,
          min: 0,
          step: 10000,
          helpText: 'Initial debt/loan amount',
        },
        {
          id: 'annualInterestRate',
          label: 'Annual Interest Rate (%)',
          type: 'number',
          defaultValue: 8,
          min: 0,
          max: 30,
          step: 0.1,
          helpText: 'Annual interest rate on debt',
        },
        {
          id: 'debtRepaymentYears',
          label: 'Debt Repayment Years',
          type: 'number',
          defaultValue: 7,
          min: 1,
          max: 30,
          step: 1,
          helpText: 'Loan repayment period (years)',
        },
      ],
    },

    // === GROUP 10: Working Capital ===
    {
      id: 'working-capital',
      title: '💰 Working Capital',
      fields: [
        {
          id: 'daysInAccountsReceivable',
          label: 'Days in Accounts Receivable',
          type: 'number',
          defaultValue: 30,
          min: 0,
          max: 365,
          step: 1,
          helpText: 'Average days to collect revenue',
        },
        {
          id: 'daysInAccountsPayable',
          label: 'Days in Accounts Payable',
          type: 'number',
          defaultValue: 45,
          min: 0,
          max: 365,
          step: 1,
          helpText: 'Average days to pay suppliers',
        },
        {
          id: 'prepaidExpensesPct',
          label: 'Prepaid Expenses (% of OpEx)',
          type: 'number',
          defaultValue: 5,
          min: 0,
          max: 100,
          step: 0.1,
        },
        {
          id: 'accruedExpensesPct',
          label: 'Accrued Expenses (% of OpEx)',
          type: 'number',
          defaultValue: 8,
          min: 0,
          max: 100,
          step: 0.1,
        },
        {
          id: 'minimumCashBalance',
          label: 'Minimum Cash Balance ($)',
          type: 'number',
          defaultValue: 100000,
          min: 0,
          step: 10000,
          helpText: 'Minimum cash balance to maintain',
        },
      ],
    },

    // === GROUP 11: Capital Raising ===
    {
      id: 'capital-raising',
      title: '🚀 Capital Raising',
      fields: [
        {
          id: 'initialCapitalRaise',
          label: 'Initial Capital Raise ($)',
          type: 'number',
          defaultValue: 1500000,
          min: 0,
          step: 50000,
          helpText: 'Initial seed/Series A funding',
        },
        {
          id: 'year2CapitalRaise',
          label: 'Year 2 Capital Raise ($)',
          type: 'number',
          defaultValue: 0,
          min: 0,
          step: 50000,
        },
        {
          id: 'year3CapitalRaise',
          label: 'Year 3 Capital Raise ($)',
          type: 'number',
          defaultValue: 0,
          min: 0,
          step: 50000,
        },
        {
          id: 'year4CapitalRaise',
          label: 'Year 4 Capital Raise ($)',
          type: 'number',
          defaultValue: 0,
          min: 0,
          step: 50000,
        },
        {
          id: 'year5CapitalRaise',
          label: 'Year 5 Capital Raise ($)',
          type: 'number',
          defaultValue: 0,
          min: 0,
          step: 50000,
        },
        {
          id: 'automaticCapitalRaise',
          label: 'Automatic Capital Raise',
          type: 'checkbox',
          defaultValue: true,
          helpText: 'Automatically raise capital when cash goes negative',
        },
      ],
    },

    // === GROUP 12: Financial & Tax ===
    {
      id: 'financial-tax',
      title: '🏛️ Financial & Tax',
      fields: [
        {
          id: 'initialSetupCost',
          label: 'Initial Setup Cost ($)',
          type: 'number',
          defaultValue: 250000,
          min: 0,
          step: 10000,
          helpText: 'One-time setup costs - legal, incorporation, initial development',
        },
        {
          id: 'corporateTaxRate',
          label: 'Corporate Tax Rate (%)',
          type: 'number',
          defaultValue: 25,
          min: 0,
          max: 60,
          step: 0.1,
          helpText: 'Corporate income tax rate',
        },
      ],
    },
  ],
};

// Export default assumptions matching server defaults exactly
export const saasDefaultAssumptions = {
  monthlyRevenuePerCustomer: 485,
  annualContractPct: 35,
  annualDiscountPct: 15,
  usageFeePerCustomer: 45,
  expansionRevenueRate: 12,
  initialSetupCost: 250000,
  startingCustomers: 75,
  growthRateY2: 20,
  growthRateY3: 15,
  growthRateY4: 12,
  growthRateY5: 8,
  monthlyChurnRate: 2.5,
  hostingCostPerCustomer: 12,
  supportCostPerCustomer: 8,
  transactionFeePct: 2.9,
  startingTeamSize: 8,
  avgAnnualSalary: 105000,
  teamGrowthRate: 45,
  cac: 1850,
  salesMarketingPct: 38,
  monthlyTechCosts: 25000,
  rdPct: 18,
  gaPct: 12,
  monthlyOfficeCosts: 8000,
  corporateTaxRate: 25,
  equipmentDepreciationRate: 20,
  softwareAmortizationRate: 33,
  initialEquipmentValue: 150000,
  initialSoftwareValue: 200000,
  initialDebtAmount: 500000,
  annualInterestRate: 8,
  debtRepaymentYears: 7,
  daysInAccountsReceivable: 30,
  daysInAccountsPayable: 45,
  prepaidExpensesPct: 5,
  accruedExpensesPct: 8,
  minimumCashBalance: 100000,
  initialCapitalRaise: 1500000,
  year2CapitalRaise: 0,
  year3CapitalRaise: 0,
  year4CapitalRaise: 0,
  year5CapitalRaise: 0,
  automaticCapitalRaise: true,
};
